import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { Package } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import CbkOrderList from './CbkOrderList';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pop up bisa ditutup ("Nanti"), tapi muncul lagi setelah jeda ini selama pertanyaan belum dijawab.
const SNOOZE_MS = 60 * 60 * 1000;
const POLL_MS = 60 * 1000;

// Halaman isian: jangan diganggu pop up saat nakes sedang mengisi atau mengetik.
const FORM_PATH = /\/jobs\/[^/]+\/(workflow|stock-usage|service-confirmation|upload-[^/]+)|\/logistic-returns\/create|\/chats\/[^/]+|\/job-discussions\/[^/]+/;

const snoozeKey = (uid) => `vaxa:cbk_question_snooze:${uid}`;

function readSnooze(uid) {
  try {
    const p = JSON.parse(localStorage.getItem(snoozeKey(uid)) || 'null');
    return { at: Number(p?.at) || 0, ids: Array.isArray(p?.ids) ? p.ids : [] };
  } catch (_e) {
    return { at: 0, ids: [] };
  }
}

function writeSnooze(uid, ids) {
  try {
    localStorage.setItem(snoozeKey(uid), JSON.stringify({ at: Date.now(), ids }));
  } catch (_e) {
    /* storage tidak tersedia: jeda tetap jalan lewat memori */
  }
}

/**
 * Pop up pertanyaan SPV soal barang keluar (Cek Barang Keluar, Tahap 3b).
 * Muncul di halaman mana pun di area nakes selama ada pertanyaan yang belum dijawab,
 * kecuali halaman isian. Bisa ditutup, muncul lagi 1 jam kemudian. Pertanyaan BARU langsung muncul.
 */
export default function CbkQuestionsPopup() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const uid = user?.user_id;
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);

  const groupsRef = useRef([]);
  const openRef = useRef(false);
  const pathRef = useRef(location.pathname);
  const closedRef = useRef({ at: 0, ids: [] });

  useEffect(() => { groupsRef.current = groups; }, [groups]);
  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => { pathRef.current = location.pathname; }, [location.pathname]);

  const allIds = (gs) => gs.flatMap((g) => g.questions.map((q) => q.kasus_id));

  const maybeOpen = useCallback(() => {
    const gs = groupsRef.current;
    if (!uid || gs.length === 0 || openRef.current) return;
    if (FORM_PATH.test(pathRef.current || '')) return;
    const stored = readSnooze(uid);
    const mem = closedRef.current;
    const lastClosed = Math.max(stored.at, mem.at);
    const seen = new Set([...(stored.ids || []), ...(mem.ids || [])]);
    const hasNew = allIds(gs).some((id) => !seen.has(id));
    if (!hasNew && Math.abs(Date.now() - lastClosed) < SNOOZE_MS) return;
    setOpen(true);
  }, [uid]);

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/cek-barang-keluar/pertanyaan-saya`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const gs = res.data.groups || [];
      setGroups(gs);
      groupsRef.current = gs;
      if (gs.length === 0) setOpen(false);
      else maybeOpen();
    } catch (_e) {
      // diam: pop up ini tambahan
    }
  }, [maybeOpen]);

  useEffect(() => {
    if (!uid) return undefined;
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [uid, load]);

  // Pindah dari halaman isian ke halaman lain: cek lagi tanpa menunggu polling berikutnya.
  useEffect(() => {
    // Masuk halaman isian saat pop up terbuka (tombol kembali, deep link): tutup tanpa menghitung jeda.
    if (FORM_PATH.test(location.pathname || '')) setOpen(false);
    else maybeOpen();
  }, [location.pathname, maybeOpen]);

  const handleClose = () => {
    const ids = allIds(groupsRef.current);
    closedRef.current = { at: Date.now(), ids };
    if (uid) writeSnooze(uid, ids);
    setOpen(false);
  };

  // Buka workflow order untuk memeriksa dulu. Jeda 1 jam tetap dicatat; pertanyaan muncul lagi sesudahnya.
  const goWorkflow = (orderId) => {
    handleClose();
    navigate(`/nakes/jobs/${orderId}/workflow`);
  };

  if (groups.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-md" data-testid="cbk-questions-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-purple-800">
            <Package className="w-5 h-5" />
            Konfirmasi Barang Keluar
          </DialogTitle>
          <DialogDescription>
            SPV menanyakan barang di bawah. Jawab sesuai yang benar-benar keluar, laporan pengeluaranmu otomatis disesuaikan.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] overflow-y-auto">
          <CbkOrderList groups={groups} onDone={load} onWorkflow={goWorkflow} testPrefix="cbk-popup" rowClass="bg-purple-50" />
        </div>

        <Button variant="outline" className="w-full" onClick={handleClose} data-testid="cbk-popup-later">
          Nanti
        </Button>
      </DialogContent>
    </Dialog>
  );
}
